import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { PersonDetailsForm } from '../../../../components/person/PersonDetailsForm';
import type { PersonDetailsValue } from '../../../../components/person/personDetails';
import type { AllocationTarget } from '../../../../components/admin/AllocationTargetField';

/**
 * Step 1. Creating the person is what unlocks the rest of the wizard, so
 * once that has happened this step stops being a form and becomes a
 * receipt: the record now exists and further edits belong on it, not here.
 */
export function DetailsStep({
  value,
  onChange,
  target,
  onTargetChange,
  personId,
  personName,
}: {
  value: PersonDetailsValue;
  onChange: (next: PersonDetailsValue) => void;
  target: AllocationTarget;
  onTargetChange: (next: string | null) => void;
  personId: string | null;
  personName: string | null;
}) {
  if (personId) {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-3 rounded-token border border-success/20 bg-success-soft/15 px-4 py-3 text-sm text-success-soft-fg">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>{personName ?? 'This person'}</strong> has been created.
            Carry on through the wizard to set up their onboarding.
          </span>
        </div>
        <p className="text-xs text-fg-muted">
          Need to correct a detail?{' '}
          <Link
            to={`/admin/people/${personId}`}
            className="font-medium underline-offset-2 hover:underline"
          >
            Edit their record
          </Link>
          . You will not lose your place here.
        </p>
      </div>
    );
  }

  return (
    <PersonDetailsForm
      value={value}
      onChange={onChange}
      target={target}
      onTargetChange={onTargetChange}
      // The wizard is the onboarding path by definition; an existing member
      // of staff collecting kit belongs on the quick add page instead.
      showExistingStaffToggle={false}
      autoFocus
    />
  );
}
